import React, { useState } from 'react';

export const SNOOZE_OPTIONS = [
  { minutes: 5, label: '5 min' },
  { minutes: 10, label: '10 min' },
  { minutes: 15, label: '15 min' },
  { minutes: 20, label: '20 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 45, label: '45 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 90, label: '1.5 hour' },
  { minutes: 120, label: '2 hour' },
  { minutes: 240, label: '4 hour' },
];

export const formatSnoozeDuration = (minutes) => {
  const n = Number(minutes);
  const preset = SNOOZE_OPTIONS.find((option) => option.minutes === n);
  if (preset) return preset.label;
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 60) return `${n} min`;
  const hours = n / 60;
  if (Number.isInteger(hours)) return `${hours} hour${hours === 1 ? '' : 's'}`;
  return `${hours} hours`;
};

const SnoozeDropdown = ({
  onSnooze,
  disabled = false,
  size = 'md',
}) => {
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [busy, setBusy] = useState(false);

  const apply = async (minutes) => {
    const n = Math.round(Number(minutes));
    if (!Number.isFinite(n) || n < 1 || n > 10080 || !onSnooze) return;
    setBusy(true);
    try {
      await onSnooze(n);
      setCustomOpen(false);
      setCustomValue('');
    } finally {
      setBusy(false);
    }
  };

  const compact = size === 'sm';
  const selectClass = compact
    ? 'max-w-[7.5rem] px-2 py-1 text-[11px] font-semibold rounded-lg border border-amber-200 dark:border-amber-800 bg-white dark:bg-gray-900 text-amber-800 dark:text-amber-200 disabled:opacity-50'
    : 'px-3 py-2 text-sm font-semibold rounded-xl border border-amber-200 dark:border-amber-800 bg-white dark:bg-gray-900 text-amber-800 dark:text-amber-200 disabled:opacity-50';

  return (
    <div className="min-w-0" onClick={(e) => e.stopPropagation()}>
      <select
        aria-label="Snooze reminder"
        disabled={disabled || busy || !onSnooze}
        value=""
        onChange={(e) => {
          const value = e.target.value;
          if (value === 'custom') {
            setCustomOpen(true);
            return;
          }
          if (value) apply(value);
        }}
        className={selectClass}
      >
        <option value="">{busy ? 'Snoozing…' : 'Snooze'}</option>
        {SNOOZE_OPTIONS.map((option) => (
          <option key={option.minutes} value={option.minutes}>
            {option.label}
          </option>
        ))}
        <option value="custom">Custom</option>
      </select>
      {customOpen ? (
        <div className={`flex items-center gap-1.5 ${compact ? 'mt-1.5' : 'mt-2'}`}>
          <input
            type="number"
            min={1}
            max={10080}
            step={1}
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            placeholder="Minutes"
            disabled={busy}
            className={`w-24 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-gray-900 dark:text-white ${
              compact ? 'text-[11px]' : 'text-sm'
            }`}
          />
          <button
            type="button"
            onClick={() => apply(customValue)}
            disabled={busy || !customValue}
            className={`font-semibold text-amber-800 dark:text-amber-200 disabled:opacity-50 ${
              compact ? 'text-[11px]' : 'text-sm'
            }`}
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              setCustomOpen(false);
              setCustomValue('');
            }}
            disabled={busy}
            className={`font-medium text-gray-500 dark:text-gray-400 ${
              compact ? 'text-[11px]' : 'text-sm'
            }`}
          >
            Cancel
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default SnoozeDropdown;
